import { useDeleteConfirmation } from '@src/hooks/useDeleteConfirmation';
import {
  MixpanelEvent,
  NotificationVariants,
  useDynamicCallback,
  useGlobalToasts,
  useMixpanel,
  type IHedgeRule,
} from '@talos/kyoko';
import type { ReactNode } from 'react';
import { useSubAccounts } from '../../../../providers';
import { useHedgeRuleDeletionMutation } from './queries';

export function getHedgeRuleDeleteItemName(hedgeRule: IHedgeRule, rollupName?: string): ReactNode {
  return (
    <>
      the <strong>{hedgeRule.Asset}</strong> hedge rule
      {rollupName ? (
        <>
          {' '}
          on <strong>{rollupName}</strong>
        </>
      ) : null}
    </>
  );
}

export function useDeleteHedgeRule() {
  const mixpanel = useMixpanel();
  const { confirmDelete } = useDeleteConfirmation();
  const { add: addToast } = useGlobalToasts();
  const { subAccountsByID } = useSubAccounts();
  const deleteHedgeRuleMutation = useHedgeRuleDeletionMutation();

  const deleteHedgeRule = useDynamicCallback((hedgeRule: IHedgeRule, options?: { onSuccess?: () => void }) => {
    const rollupName = subAccountsByID.get(hedgeRule.AccountID)?.Name;
    confirmDelete({
      title: 'Delete Hedge Rule',
      itemName: getHedgeRuleDeleteItemName(hedgeRule, rollupName),
      onConfirm: () => {
        mixpanel.track(MixpanelEvent.ClickDeleteHedgeRule);
        deleteHedgeRuleMutation
          .mutateAsync(hedgeRule.HedgeRuleID)
          .then(() => {
            addToast({
              text: 'Hedge rule deleted.',
              variant: NotificationVariants.Positive,
            });
            options?.onSuccess?.();
          })
          .catch(error => {
            addToast({
              text: error?.toString() || 'Failed to delete.',
              variant: NotificationVariants.Negative,
            });
          });
      },
    });
  });

  return {
    deleteHedgeRule,
    isDeleting: deleteHedgeRuleMutation.isPending,
  };
}
